import React, { FC } from 'react';
import styled from 'styled-components';
import Stack from 'src/components/widgets/stack/stack';
import { AiRotatingThinking, AiThinkingLine } from 'src/components/shared/aiAssistant';
import { AiOutlineButton } from 'src/components/widgets/ai';
import { GhostButton } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import { AgentAnswerRecap, AgentResultRecap, AgentTurnRequest } from './types';

const Card = styled(Stack)`
    min-width: 0;
    padding: 12px;
    border: 1px solid #dce4e8;
    border-radius: 10px;
    background: #fafcfd;
    color: #37474f;
    font-size: 12px;
`;
const Rows = styled.dl`
    display: grid;
    grid-template-columns: minmax(75px, 0.8fr) minmax(0, 1.6fr);
    gap: 8px 12px;
    margin: 0;
    dt {
        color: #607d8b;
        overflow-wrap: anywhere;
    }
    dd {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }
`;
const Details = styled.details`
    min-width: 0;
    summary {
        cursor: pointer;
        color: #546e7a;
        font-size: 11px;
    }
    ul {
        padding-left: 18px;
        margin: 8px 0 0;
    }
    li {
        padding: 3px 0;
        overflow-wrap: anywhere;
    }
`;
// Rendered inside the merchant bubble: no card chrome of its own.
const AnswerRecap = styled(Stack)`
    min-width: 0;
    font-size: 12px;
`;
// A text action with no side padding, so it lines up with the title, the rows and "Show questions".
const AnswerEditButton = styled(GhostButton)`
    && {
        align-self: flex-start;
        height: auto;
        min-height: 0;
        padding: 0;
        border: 0;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.5;
    }
    &&:hover:not([disabled]),
    &&:active:not([disabled]) {
        background-color: transparent;
        text-decoration: underline;
    }
`;
const Activity = styled(Stack)`
    padding: 10px 4px;
`;
const Actions = styled(Stack)`
    flex-wrap: wrap;
`;

const activityLabel = (code: string): string =>
    ({
        thinking: T._('Processing your request…', 'Admin'),
        prepare_response: T._('Preparing the response…', 'Admin'),
        validate_draft: T._('Checking the draft…', 'Admin'),
        read_product: T._('Reading the product configuration…', 'Admin'),
        read_store_product: T._('Reading store products…', 'Admin'),
        read_knowledge: T._('Searching Zakeke knowledge…', 'Admin'),
        import_store_product: T._('Importing the store product…', 'Admin'),
        read_printing_method: T._('Checking printing methods…', 'Admin'),
        read_pricing: T._('Checking pricing settings…', 'Admin'),
        generate_images: T._('Generating product images…', 'Admin'),
        check_images: T._('Checking generated images…', 'Admin'),
        detect_areas: T._('Finding print areas…', 'Admin'),
        edit_areas: T._('Configuring print areas…', 'Admin'),
        set_scale: T._('Setting the image scale…', 'Admin'),
        edit_variants: T._('Configuring product variants…', 'Admin'),
        edit_sides: T._('Configuring product sides…', 'Admin'),
        edit_printing_method: T._('Preparing the printing method…', 'Admin'),
        edit_pricing: T._('Preparing pricing rules…', 'Admin'),
        validate_plan: T._('Verifying the proposed changes…', 'Admin'),
        apply_product: T._('Applying the approved product changes…', 'Admin'),
        apply_printing_method: T._('Applying the approved printing method changes…', 'Admin'),
        apply_pricing: T._('Applying the approved pricing changes…', 'Admin'),
        edit_draft: T._('Updating the draft…', 'Admin')
    })[code] ?? T._('Working on your request…', 'Admin');

const resultLabel = (code: string): string =>
    ({
        edit_areas: T._('Print areas', 'Admin'),
        detect_areas: T._('Print area detection', 'Admin'),
        set_scale: T._('Image scale', 'Admin'),
        edit_variants: T._('Product variants', 'Admin'),
        edit_sides: T._('Product sides and images', 'Admin'),
        edit_printing_method: T._('Printing method configuration', 'Admin'),
        edit_pricing: T._('Pricing configuration', 'Admin'),
        edit_draft: T._('Draft changes', 'Admin'),
        generate_images: T._('Image generation', 'Admin'),
        check_images: T._('Generated images', 'Admin'),
        apply_product: T._('Product changes', 'Admin'),
        apply_printing_method: T._('Printing method changes', 'Admin'),
        apply_pricing: T._('Pricing changes', 'Admin'),
        validate_plan: T._('Change validation', 'Admin'),
        read_product: T._('Product lookup', 'Admin'),
        read_store_product: T._('Store product lookup', 'Admin'),
        read_knowledge: T._('Knowledge lookup', 'Admin'),
        import_store_product: T._('Store product import', 'Admin'),
        read_printing_method: T._('Printing method lookup', 'Admin'),
        read_pricing: T._('Pricing lookup', 'Admin')
    })[code] ?? T._('Operation', 'Admin');

export const AgentActivityIndicator: FC<{
    request: AgentTurnRequest | null;
    restoring: boolean;
    refreshing: boolean;
    /** Rotating phrases instead of the single step label, like the other AI drawers (e.g. while an approved plan is applied). */
    phrases?: string[];
}> = ({ request, restoring, refreshing, phrases }) => {
    if (phrases && phrases.length > 0 && !restoring && !refreshing)
        return (
            <Activity role='status' aria-live='polite'>
                <AiRotatingThinking phrases={phrases} />
            </Activity>
        );

    const activities = request?.progress?.activities ?? [];
    // The running step; between two steps, the last one stays until the next replaces it.
    const current = [...activities].reverse().find((activity) => activity.state === 'running') ?? activities[activities.length - 1];
    const label = restoring
        ? T._('Restoring your conversation…', 'Admin')
        : refreshing
          ? T._('Updating the artifact…', 'Admin')
          : request?.cancellationRequested
            ? T._('Stopping the request…', 'Admin')
          : request?.state === 'queued'
            ? T._('Waiting to start your request…', 'Admin')
            : current
              ? activityLabel(current.code)
              : T._('Processing your request…', 'Admin');
    return (
        <Activity>
            {/* Keyed by the label, so each new step remounts and plays its entrance. */}
            <AiThinkingLine key={label} role='status' aria-live='polite' aria-atomic='true'>
                {label}
            </AiThinkingLine>
        </Activity>
    );
};

export const AgentAnswerRecapCard: FC<{ recap: AgentAnswerRecap; onEdit?: () => void; disabled?: boolean }> = ({ recap, onEdit, disabled }) => (
    <AnswerRecap $gap='10px'>
        <strong>{recap.correctionAfterTurnId ? T._('Updated answers sent', 'Admin') : T._('Answers sent', 'Admin')}</strong>
        <Rows>
            {recap.answers.map((answer) => (
                <React.Fragment key={answer.questionId}>
                    <dt>{answer.title}</dt>
                    <dd>{answer.skipped ? T._('Skipped', 'Admin') : answer.text || answer.labels.join(', ')}</dd>
                </React.Fragment>
            ))}
        </Rows>
        <Details>
            <summary>{T._('Show questions', 'Admin')}</summary>
            <ul>
                {recap.answers.map((answer) => (
                    <li key={answer.questionId}>{answer.question}</li>
                ))}
            </ul>
        </Details>
        {onEdit && (
            <AnswerEditButton type='button' $size='small' disabled={disabled} onClick={onEdit}>
                {T._('Edit answers', 'Admin')}
            </AnswerEditButton>
        )}
    </AnswerRecap>
);

export const AgentResultRecapCard: FC<{ recap: AgentResultRecap; onPreview?: () => void }> = ({ recap, onPreview }) => {
    const title =
        recap.state === 'partial'
            ? T._('Some steps need attention', 'Admin')
            : recap.state === 'approval_required'
              ? T._('Changes prepared for approval', 'Admin')
              : recap.state === 'applied'
                ? T._('Changes applied', 'Admin')
                : T._('Draft updated', 'Admin');
    const stateLabel = (state: string) =>
        state === 'applied' ? T._('Applied', 'Admin') : state === 'failed' ? T._('Not completed', 'Admin') : T._('Saved in draft', 'Admin');
    return (
        <Card $gap='10px'>
            <strong>{title}</strong>
            <Rows>
                {recap.items.slice(0, 3).map((item, index) => (
                    <React.Fragment key={index}>
                        <dt>{resultLabel(item.code)}</dt>
                        <dd>{stateLabel(item.state)}</dd>
                    </React.Fragment>
                ))}
            </Rows>
            <Details>
                <summary>{T._('Show details', 'Admin')}</summary>
                <ul>
                    {recap.items.map((item, index) => (
                        <li key={index}>
                            {resultLabel(item.code)} · {stateLabel(item.state)} · {item.count} {T._('operations', 'Admin')}
                        </li>
                    ))}
                </ul>
            </Details>
            {onPreview && (
                <Actions $horizontal $gap='8px'>
                    <AiOutlineButton type='button' $size='small' onClick={onPreview}>
                        {T._('See artifact', 'Admin')}
                    </AiOutlineButton>
                </Actions>
            )}
        </Card>
    );
};
